import { Button, Tooltip, Box, Tabs, Tab, useMediaQuery } from '@mui/material';
import { Add, Delete, Edit, Update } from '@mui/icons-material';
import {
  Page,
  PageTitleWithButton,
  ExternalLink,
  Table,
  TabsBar,
  MobileTable,
  Pagination
} from '../../../../components';
import { useState, useMemo } from 'react';
import {
  IContentLibraryDocument,
  ISortDirection,
  IResponse,
  IVendorType,
  ISubcontractorType,
  IDropdownResponse,
  IContentLibraryMissingDoc,
  IAvailableTemplateTypes
} from '../../../../models';
import {
  getContentLibraryDocuments,
  deleteContentLibraryDoc,
  getVendorTypes,
  getPlaceHolderBlocks,
  getContentLibraryMissingDocuments,
  getContentLibraryAvailableTypes,
  getAdminSubcontractorsTypes
} from '../../../../fetch';
import { AssignModal } from '../assign-modal';
import { TemplateLibraryFilters } from '../template-library-filters';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { MissingTemplateLibraryFilters } from '../missing-template-library-filters';
import { formatDate } from '../../../../helpers';

enum TemplateTabs {
  Assigned,
  Pending,
  ReplacementFields
}

export const ContentLibrariesPage = () => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isAssignContentLibraryModalOpen, showAssignContentLibraryModal] = useState(false);
  const [contentLibrary, setCurrentContentLibrary] = useState<IContentLibraryDocument | null>(null);
  const [partnerType, setPartnerType] = useState<string | undefined>('');
  const [selectedDocumentType, setDocumentType] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [subcontractorTypeId, setSubcontractorTypeId] = useState<string | number | undefined>('');
  const [searchValue, setSearchValue] = useState('');
  const [searchedValue, setSearchedValue] = useState('');
  const [filters, setFilters] = useState<{
    search?: string;
    vendorTypeId?: number | string;
    subcontractorTypeId?: number | string;
    contentPlaceHolderBlock?: string;
  }>({});
  const [missingContentLibrarySearch, setMissingContentLibrarySearch] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    DocumentName?: ISortDirection;
    ContentPlaceHolderBlock?: ISortDirection;
    VendorType?: ISortDirection;
    SubcontractorType?: ISortDirection;
  }>({
    DocumentName: 'Asc'
  });

  const {
    isLoading,
    data,
    refetch: fetchLibraryDocs
  } = useQuery<IResponse<IContentLibraryDocument[]>, Error>(
    [
      'getContentLibraryDocuments',
      selectedSort,
      sortDirection,
      page,
      perPage,
      filters,
      searchedValue
    ],
    () =>
      getContentLibraryDocuments({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        search: searchedValue,
        ...filters
      }),
    {
      onSuccess: d => setRecordCount(d.totalRecordCount),
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingVendorData, data: vendorTypeData } = useQuery<
    IResponse<IVendorType[]>,
    Error
  >(['getVendorTypes'], () => getVendorTypes());

  const { isLoading: isLoadingSubcontractorData, data: subcontractorTypeData } = useQuery<
    IResponse<ISubcontractorType[]>,
    Error
  >(['getSubcontractorsTypes'], () => getAdminSubcontractorsTypes());

  const { isLoading: isLoadingPlaceHolderBlocksData, data: placeHolderBlocksData } = useQuery<
    IDropdownResponse[],
    Error
  >(['getPlaceHolderBlocks'], () => getPlaceHolderBlocks());

  const {
    isLoading: isLoadingMissingLibraries,
    data: missingContentLibraries,
    refetch: fetchMissingContentLibraries
  } = useQuery<IContentLibraryMissingDoc[], Error>(
    ['getContentLibraryMissingDocuments', missingContentLibrarySearch],
    () =>
      getContentLibraryMissingDocuments({
        query: missingContentLibrarySearch
      }),
    {
      onError: (d: any) => {
        enqueueSnackbar(d?.Detail ?? `Error loading missing content libraries, please try again.`, {
          variant: 'error'
        });
      }
    }
  );

  const {
    isLoading: isLoadingContentTypes,
    data: contentTypes,
    refetch: refetchContentTypes
  } = useQuery<IAvailableTemplateTypes[], Error>(['getContentLibraryAvailableTypes'], () =>
    getContentLibraryAvailableTypes()
  );

  const contentLibraries: IContentLibraryDocument[] = useMemo(() => data?.records, [data]);
  const vendorTypes: IVendorType[] = useMemo(() => vendorTypeData?.records, [vendorTypeData]);
  const subcontractorTypes: ISubcontractorType[] = useMemo(
    () => subcontractorTypeData?.records,
    [subcontractorTypeData]
  );

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleDelete = async (option: IContentLibraryDocument) => {
    const result = window.confirm('Are you sure you want to remove this assignment?');
    if (result) {
      try {
        setIsDeleting(true);
        await deleteContentLibraryDoc(option.pandaDocContentLibraryDocumentId);
        fetchLibraryDocs();
        fetchMissingContentLibraries();
        refetchContentTypes();
        enqueueSnackbar('Content library assignment removed.', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(
          error?.Detail ?? `Error removing this content library assignment, please try again.`,
          {
            variant: 'error'
          }
        );
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleUpdate = (contentLibrary: IContentLibraryDocument) => {
    setCurrentContentLibrary(contentLibrary);
    showAssignContentLibraryModal(true);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Library',
        accessor: 'documentName',
        isServerSorted: selectedSort === 'DocumentName',
        isServerSortedDesc: sortDirection.DocumentName === 'Desc',
        handleClickColumn: () => handleClickColumn('DocumentName')
      },
      {
        Header: 'Document Type',
        accessor: 'contentPlaceHolderBlockDisplayName',
        isServerSorted: selectedSort === 'ContentPlaceHolderBlock',
        isServerSortedDesc: sortDirection.ContentPlaceHolderBlock === 'Desc',
        handleClickColumn: () => handleClickColumn('ContentPlaceHolderBlock')
      },
      {
        Header: 'Vendor Type',
        accessor: 'vendorType',
        isServerSorted: selectedSort === 'VendorType',
        isServerSortedDesc: sortDirection.VendorType === 'Desc',
        handleClickColumn: () => handleClickColumn('VendorType')
      },
      {
        Header: 'Subcontractor Type',
        accessor: row => row.subcontractorTypes.map(obj => obj.description).join(', '),
        isServerSorted: selectedSort === 'SubcontractorType',
        isServerSortedDesc: sortDirection.SubcontractorType === 'Desc',
        handleClickColumn: () => handleClickColumn('SubcontractorType')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IContentLibraryDocument } };
        }) => {
          return (
            <Box display="flex">
              <Tooltip title="Edit PandaDoc Content Library">
                <div>
                  <ExternalLink
                    target={'_blank'}
                    to={`https://app.pandadoc.com/a/#/library/${original.pandaDocContentLibraryDocumentId}`}
                  >
                    <Button id="edit" color="primary" startIcon={<Edit />}>
                      Edit
                    </Button>
                  </ExternalLink>
                </div>
              </Tooltip>
              <Tooltip title="Update Content Library Assignment">
                <Button
                  color="primary"
                  startIcon={<Update />}
                  onClick={() => {
                    handleUpdate(original);
                  }}
                >
                  Update
                </Button>
              </Tooltip>
              <Button
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original);
                }}
              >
                Unassign
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentLibraries, selectedSort, sortDirection]);

  const missingColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Date Created',
        accessor: 'date_created',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IContentLibraryMissingDoc } };
        }) => {
          return <span>{formatDate(original.date_created)}</span>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IContentLibraryMissingDoc } };
        }) => {
          return (
            <Box display="flex">
              <ExternalLink
                target={'_blank'}
                to={`https://app.pandadoc.com/a/#/library/${original.id}`}
              >
                <Button id="edit" color="primary" startIcon={<Edit />}>
                  Edit PandaDoc Content Library
                </Button>
              </ExternalLink>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingContentLibraries]);
  return (
    <Page title="Content Libraries">
      <PageTitleWithButton
        pageTitleBackProps={{
          showDivider: false,
          backText: 'Back to Admin home',
          backUrl: '/admin',
          title: 'Content Libraries'
        }}
        button={
          <>
            {selectedTab === TemplateTabs.Pending && (
              <ExternalLink
                target={'_blank'}
                to={
                  'https://app.pandadoc.com/a/#/library-next?folderId=w6nyDcDXP9bSuenAaCfvof&sortBy=name&direction=asc&displayMode=folders_first&mainFilter=all'
                }
              >
                <Button
                  id="add-contract-template"
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                >
                  Add Content Library
                </Button>
              </ExternalLink>
            )}
            {selectedTab === TemplateTabs.Assigned && (
              <Button
                id="create-contract-library"
                startIcon={<Add />}
                onClick={() => {
                  showAssignContentLibraryModal(true);
                }}
                color="primary"
                variant="contained"
              >
                Assign Content Library
              </Button>
            )}
          </>
        }
      />
      <Box mt={-1}>
        <TabsBar>
          <Tabs
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_, value) => {
              setSelectedTab(value);
            }}
          >
            <Tab id="assigned" aria-controls="assigned" label="Assigned" />
            <Tab id="pending" aria-controls="pending" label="Pending" />
          </Tabs>
        </TabsBar>
      </Box>
      {selectedTab === TemplateTabs.Pending && (
        <Box mt={1}>
          <MissingTemplateLibraryFilters
            isLoading={false}
            searchValue={missingContentLibrarySearch}
            setSearch={setMissingContentLibrarySearch}
          />
        </Box>
      )}
      {selectedTab === TemplateTabs.Assigned && (
        <Box mt={1}>
          <TemplateLibraryFilters
            isLoading={false}
            handleSearch={(clearSearch?: boolean) => {
              setPage(0);
              setFilters({
                vendorTypeId: partnerType as string,
                subcontractorTypeId,
                contentPlaceHolderBlock: selectedDocumentType || ''
              });
            }}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setFilters({
                  vendorTypeId: '',
                  subcontractorTypeId: undefined,
                  contentPlaceHolderBlock: ''
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  vendorTypeId: partnerType || undefined,
                  subcontractorTypeId: subcontractorTypeId || undefined,
                  contentPlaceHolderBlock: selectedDocumentType || ''
                });
              }
            }}
            searchValue={searchValue}
            searchedValue={searchedValue}
            setSearch={setSearchValue}
            setSearchedValue={setSearchedValue}
            setPage={setPage}
            isLoadingPartnerTypes={isLoadingVendorData}
            setPartnerType={setPartnerType}
            partnerType={partnerType}
            isLoadingSubcontractorTypes={isLoadingSubcontractorData}
            setSubcontractorTypeId={setSubcontractorTypeId}
            subcontractorTypeId={subcontractorTypeId}
            partnerTypes={vendorTypes}
            subcontractorTypes={subcontractorTypes}
            setDocumentType={setDocumentType}
            selectedDocumentType={selectedDocumentType}
            isLoadingTemplateTypes={isLoadingPlaceHolderBlocksData}
            templateTypes={placeHolderBlocksData?.map(d => ({
              type: d.value,
              typeDisplayName: d.description,
              vendorTypes: []
            }))}
          />
        </Box>
      )}
      {selectedTab === TemplateTabs.Pending && (
        <Table
          columns={missingColumns}
          data={missingContentLibraries || []}
          isLoading={isLoadingMissingLibraries}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: missingColumns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              })),
            handleEdit: (val: IContentLibraryMissingDoc) => {
              window.open(`https://app.pandadoc.com/a/#/library/${val.id}`, 'blank');
            }
          }}
        />
      )}
      {selectedTab === TemplateTabs.Assigned && (
        <>
          <Table
            columns={columns}
            data={contentLibraries || []}
            isLoading={isLoading || isDeleting}
            ResponsiveComponent={isMobile ? MobileTable : undefined}
            hidePagination
            mobileProps={{
              fields: columns
                .filter(c => c.Header)
                .map(c => ({
                  name: c.Header,
                  accessor: c.accessor,
                  Cell: c?.Cell
                })),
              handleDelete: (val: IContentLibraryDocument) => handleDelete(val),
              handleEdit: (val: IContentLibraryDocument) => handleUpdate(val),
              handleUpdate: (val: IContentLibraryDocument) => handleUpdate(val)
            }}
          />
          {!isLoading && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </>
      )}
      {isAssignContentLibraryModalOpen && (
        <AssignModal
          open={true}
          onClose={(shouldUpdate?: boolean) => {
            showAssignContentLibraryModal(false);
            setCurrentContentLibrary(null);
            if (shouldUpdate) {
              fetchLibraryDocs();
              fetchMissingContentLibraries();
            }
          }}
          missingDocs={missingContentLibraries}
          isLoadingMissingDocs={isLoadingMissingLibraries}
          isLoadingTypes={isLoadingContentTypes}
          refetchContentTypes={refetchContentTypes}
          types={contentTypes}
          currentOption={contentLibrary}
          isContentLibrary
        />
      )}
    </Page>
  );
};
